import type { InterviewSectionDefinition } from "@repo/app-types/entities/interviewSection/definition";
import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import {
  TagType,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
} from "../helpers/tags.js";
import { api } from "../redux.js";

export const interviewSectionDefinitionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allInterviewSectionDefinitionsForJobType: builder.query<
      InterviewSectionDefinition[],
      { jobTypeId: string },
      { data: { interview_section_definitions: InterviewSectionDefinition[] } }
    >({
      query: ({ jobTypeId }) =>
        `/job-types/${encodeURIComponent(jobTypeId)}/sections`,
      transformResponse: (response) =>
        response.data.interview_section_definitions,
      providesTags: (result, error, { jobTypeId }) =>
        getTagsProvidedByList(TagType.InterviewSectionDefinition, result, {
          filteredBy: [jobTypeId],
        }),
    }),
    createInterviewSectionDefinition: builder.mutation<
      InterviewSectionDefinition,
      { jobTypeId: string; settings: InterviewSectionSettings },
      { data: { interview_section_definition: InterviewSectionDefinition } }
    >({
      query: ({ jobTypeId, ...values }) => ({
        method: "POST",
        url: `/job-types/${encodeURIComponent(jobTypeId)}/sections`,
        body: values,
      }),
      transformResponse: (response) =>
        response.data.interview_section_definition,
      onQueryStarted: async ({ jobTypeId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: interviewSectionDefinition } = await queryFulfilled;
          dispatch(
            interviewSectionDefinitionApi.util.updateQueryData(
              "allInterviewSectionDefinitionsForJobType",
              { jobTypeId },
              (draft) => {
                draft.push(interviewSectionDefinition);
              },
            ),
          );
        } catch {
          // Ignore.
        }
      },
      invalidatesTags: (result, error, { jobTypeId }) =>
        getTagsInvalidatedByCreate(TagType.InterviewSectionDefinition, {
          inLists: [{ filteredBy: [jobTypeId] }],
        }),
    }),
    updateInterviewSectionDefinition: builder.mutation<
      InterviewSectionDefinition,
      { jobTypeId: string; id: string; settings: InterviewSectionSettings },
      { data: { interview_section_definition: InterviewSectionDefinition } }
    >({
      query: ({ jobTypeId, id, ...values }) => ({
        method: "PATCH",
        url: `/job-types/${encodeURIComponent(jobTypeId)}/sections/${encodeURIComponent(
          id,
        )}`,
        body: values,
      }),
      transformResponse: (response) =>
        response.data.interview_section_definition,
      onQueryStarted: async ({ jobTypeId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: interviewSectionDefinition } = await queryFulfilled;
          dispatch(
            interviewSectionDefinitionApi.util.updateQueryData(
              "allInterviewSectionDefinitionsForJobType",
              { jobTypeId },
              (draft) => {
                const index = draft.findIndex(
                  (section) => section.id === interviewSectionDefinition.id,
                );
                if (index !== -1) {
                  draft[index] = interviewSectionDefinition;
                }
              },
            ),
          );
        } catch {
          // Ignore.
        }
      },
      invalidatesTags: (result, error, { id, jobTypeId }) =>
        getTagsInvalidatedByUpdate(TagType.InterviewSectionDefinition, id, {
          inLists: [{ filteredBy: [jobTypeId] }],
        }),
    }),
    deleteInterviewSectionDefinition: builder.mutation<
      void,
      { jobTypeId: string; id: string }
    >({
      query: ({ jobTypeId, id }) => ({
        method: "DELETE",
        url: `/job-types/${encodeURIComponent(jobTypeId)}/sections/${encodeURIComponent(
          id,
        )}`,
      }),
      onQueryStarted: async (
        { jobTypeId, id },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          interviewSectionDefinitionApi.util.updateQueryData(
            "allInterviewSectionDefinitionsForJobType",
            { jobTypeId },
            (draft) => {
              const index = draft.findIndex((section) => section.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, { jobTypeId, id }) =>
        getTagsInvalidatedByDelete(TagType.InterviewSectionDefinition, id),
    }),
  }),
});

export const {
  useAllInterviewSectionDefinitionsForJobTypeQuery,
  useCreateInterviewSectionDefinitionMutation,
  useUpdateInterviewSectionDefinitionMutation,
  useDeleteInterviewSectionDefinitionMutation,
} = interviewSectionDefinitionApi;
